import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Calendar, Edit } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Story {
  id: string;
  title: string;
  excerpt: string;
  content: string | null;
  category: string;
  read_time: string;
  published_at: string;
  image_url: string | null;
  featured: boolean;
  slug: string;
}

interface QnAEntry {
  id: string;
  story_id: string;
  question: string;
  answer: string;
  sort_order: number;
}

const Article = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState<Story | null>(null);
  const [qnaEntries, setQnaEntries] = useState<QnAEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();
  
  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      try {
        const { data, error } = await supabase
          .from('stories')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (error) {
          console.error('Error fetching article:', error);
          return;
        }

        setArticle(data);
        
        // Fetch Q&A entries if article exists
        if (data) {
          const { data: qnaData, error: qnaError } = await supabase
            .from('article_qna')
            .select('*')
            .eq('story_id', data.id)
            .order('sort_order', { ascending: true });

          if (!qnaError && qnaData) {
            setQnaEntries(qnaData);
          }
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-muted-foreground">Loading article...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Articles
          </Link>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground">The article you're looking for doesn't exist.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Articles
        </Link>

        {/* Article Header */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex items-start justify-between">
            <div className="flex-1">
              <Badge className="mb-4">{article.category}</Badge>
              {article.featured && (
                <Badge variant="secondary" className="ml-2">Featured</Badge>
              )}
              <h1 className="text-4xl font-bold mb-4">{article.title}</h1>
              <p className="text-xl text-muted-foreground mb-6">{article.excerpt}</p>
              
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <Calendar className="mr-2 h-4 w-4" />
                  {new Date(article.published_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="flex items-center">
                  <Clock className="mr-2 h-4 w-4" />
                  {article.read_time} read
                </div>
              </div>
            </div>
            
            {/* Admin Edit Button */}
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                asChild
                className="ml-4 flex items-center gap-2"
              >
                <a href={`/admin?edit=${article.id}`}>
                  <Edit className="h-4 w-4" />
                  Edit Article
                </a>
              </Button>
            )}
          </div>

          {/* Hero Image */}
          {article.image_url && (
            <div className="aspect-video mb-8 overflow-hidden">
              <img 
                src={article.image_url} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Article Content - with consistent spacing */}
          <div className="article-content-wrapper" style={{ marginTop: article.image_url ? '1em' : '0' }}>
            {article.content ? (
              <div 
                dangerouslySetInnerHTML={{ __html: article.content }} 
                className="article-content text-foreground"
              />
            ) : (
              <p className="text-muted-foreground">Content coming soon...</p>
            )}
          </div>

          {/* Q&A Section */}
          {qnaEntries.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <div className="h-8 w-1 bg-primary"></div>
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                {qnaEntries.map((entry, index) => (
                  <div key={entry.id} className="bg-muted/30 p-6 border border-border">
                    <h3 className="text-lg font-semibold mb-3 text-foreground">
                      Q{index + 1}: {entry.question}
                    </h3>
                    <div className="prose prose-sm max-w-none dark:prose-invert text-muted-foreground">
                      <div dangerouslySetInnerHTML={{ __html: entry.answer.replace(/\n/g, '<br>') }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Article;